import { useState } from 'react';

// Tiny relative-time formatter. No dependencies on date libraries; we want
// the smallest bundle for v1. Falls back to ISO when the input is unparseable.
const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 60 * 60 * 24 * 365],
  ['month', 60 * 60 * 24 * 30],
  ['day', 60 * 60 * 24],
  ['hour', 60 * 60],
  ['minute', 60],
  ['second', 1],
];

const RTF = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

function format(iso: string, now: number): { ok: true; text: string } | { ok: false } {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return { ok: false };
  const diffSec = Math.round((t - now) / 1000);
  // A future timestamp is almost always an artifact of our captured "now" being
  // stale — it's read once at mount and never ticks (see below), so an entity
  // updated after mount (e.g. a job just re-run) reads as the future. Clamp to
  // "just now" instead of a misleading "in N minutes".
  if (diffSec > 0) return { ok: true, text: 'just now' };
  for (const [unit, sec] of UNITS) {
    if (Math.abs(diffSec) >= sec || unit === 'second') {
      return { ok: true, text: RTF.format(Math.round(diffSec / sec), unit) };
    }
  }
  // Unreachable — loop terminates on `unit === 'second'`. Satisfy TS's
  // control-flow analysis without a runtime fallback.
  throw new Error('unreachable');
}

export function RelativeTime({ iso }: { iso: string }) {
  // Capture "now" once via useState initializer — React's purity rule allows
  // impure reads in initializers (they run once per mount, not per render).
  const [now] = useState(() => Date.now());
  const result = format(iso, now);
  if (!result.ok) return <span title={iso}>{iso}</span>;
  return (
    <time dateTime={iso} title={iso}>
      {result.text}
    </time>
  );
}
